import React, { useMemo, useState } from 'react';
import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Loader from '@amzn/meridian/loader';
import Row from '@amzn/meridian/row';
import SearchField from '@amzn/meridian/search-field';
import Table, { TableRow, TableCell } from '@amzn/meridian/table';
import Text from '@amzn/meridian/text';
import { useTranslation } from 'src/arb-wrapper';
import { SpotlightJobHistoryRow } from 'src/components/spotlight/SpotlightJobHistoryRow';
import { TablePagination } from 'src/components/common/data-table-action-bar/TablePagination';
import type { GetSpotlightJobHistoryResponse } from 'src/store/spotlight-api';

const ITEMS_PER_PAGE_OPTIONS = [10, 20, 50];
const DEFAULT_PAGE_SIZE = 10;

interface SpotlightJobHistoryProps {
  data: GetSpotlightJobHistoryResponse | undefined;
  isLoading: boolean;
  isError: boolean;
}

export const SpotlightJobHistory = ({ data, isLoading, isError }: SpotlightJobHistoryProps) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const allJobs = Array.isArray(data) ? data : [];

  const filteredJobs = useMemo(() => {
    if (!searchQuery.trim()) return allJobs;
    const query = searchQuery.toLowerCase();
    return allJobs.filter(
      (job) =>
        job.executionId?.toLowerCase().includes(query) ||
        job.seedSegmentId?.toLowerCase().includes(query) ||
        job.outputSegmentName?.toLowerCase().includes(query),
    );
  }, [allJobs, searchQuery]);

  const pageCount = Math.max(1, Math.ceil(filteredJobs.length / pagination.pageSize));
  const paginatedJobs = filteredJobs.slice(
    (pagination.page - 1) * pagination.pageSize,
    pagination.page * pagination.pageSize,
  );

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  return (
    <Column spacing="400">
      <Row alignmentHorizontal="justify" alignmentVertical="center">
        <Column spacing="100">
          <Heading level={2} type="h300">
            {t('spotlight_job_history_heading', { count: filteredJobs.length })}
          </Heading>
          <Text color="secondary" type="b200">
            {t('spotlight_job_history_description')}
          </Text>
        </Column>
      </Row>

      <SearchField
        data-testid="job-history-search"
        placeholder={t('spotlight_job_history_search_placeholder')}
        size="small"
        value={searchQuery}
        width={400}
        onChange={handleSearchChange}
        onSubmit={() => {}}
      />

      {isLoading
        ? (
        <Row alignmentHorizontal="center">
          <Loader size="large" />
        </Row>
          )
        : isError
          ? (
        <Text color="error">{t('spotlight_job_history_error')}</Text>
            )
          : (
        <>
          <Box overflowX="auto">
            <Table data-testid="job-history-table" headerRows={1} rowComponents={[TableRow, SpotlightJobHistoryRow]} showDividers={true} spacing="medium">
              <TableRow>
                <TableCell>{t('spotlight_job_history_header_execution_id')}</TableCell>
                <TableCell>{t('spotlight_job_history_header_status')}</TableCell>
                <TableCell>{t('spotlight_job_history_header_message')}</TableCell>
                <TableCell>{t('spotlight_job_history_header_output_segment_name')}</TableCell>
                <TableCell>{t('spotlight_job_history_header_country')}</TableCell>
                <TableCell>{t('spotlight_job_history_header_partner')}</TableCell>
                <TableCell>{t('spotlight_job_history_header_seed_segment_id')}</TableCell>
                <TableCell>{t('spotlight_job_history_header_exclusion_segment_id')}</TableCell>
                <TableCell>{t('spotlight_job_history_header_created_at')}</TableCell>
                <TableCell>{t('spotlight_job_history_header_updated_at')}</TableCell>
                <TableCell>{t('spotlight_job_history_header_account_ids')}</TableCell>
              </TableRow>
              {paginatedJobs.length > 0
                ? (
                    paginatedJobs.map((job) => (
                  <SpotlightJobHistoryRow job={job} key={job.executionId} />
                    ))
                  )
                : (
                <TableRow>
                  <TableCell columnSpan={12}>
                    {t('spotlight_job_history_empty')}
                  </TableCell>
                </TableRow>
                  )}
            </Table>
          </Box>

          {filteredJobs.length > 0 && (
            <TablePagination
              currentPage={pagination.page}
              itemsPerPage={pagination.pageSize}
              itemsPerPageOptions={ITEMS_PER_PAGE_OPTIONS}
              pageCount={pageCount}
              onItemsPerPageChange={(pageSize) => {
                setPagination({ page: 1, pageSize });
              }}
              onPageChange={(page) => {
                setPagination((prev) => ({ ...prev, page }));
              }}
            />
          )}
        </>
            )}
    </Column>
  );
};
